import {
  COMPONENT_VALUE_TYPES as types,
  DIRECTIONS,
} from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  editor?: object;
  background?: {
    useBackgroundColor?: boolean;
    backgroundColor?: string;
    desktopImage?: string;
    tabletImage?: string;
    mobileImage?: string;
  };
  subtitle?: {
    text?: string;
    color?: string;
    gradient?: string;
  };
  title?: {
    text?: string;
    color?: string;
    gradient?: string;
  };
  description?: {
    text?: string;
    color?: string;
  };
  animation?: {
    enabled?: boolean;
    type?: 'FADE' | 'SLIDE' | 'SCALE';
    direction?: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
    duration?: number | string;
    initialDelay?: number | string;
    stagger?: number | string;
    animateHeader?: boolean;
    animateBlocks?: boolean;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'background',
    label: 'Background',
    type: types.OBJECT,
    children: [
      {
        key: 'useBackgroundColor',
        label: 'Use Background Color',
        type: types.BOOLEAN,
      },
      {
        key: 'backgroundColor',
        label: 'Background Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'desktopImage',
        label: 'Desktop Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'tabletImage',
        label: 'Tablet Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'mobileImage',
        label: 'Mobile Background Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'subtitle',
    label: 'Subtitle',
    type: types.OBJECT,
    children: [
      {
        key: 'text',
        label: 'Subtitle Text',
        type: types.TEXT,
      },
      {
        key: 'color',
        label: 'Subtitle Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'gradient',
        label: 'Subtitle Gradient',
        type: types.TEXT,
        tooltip: 'CSS gradient string. If provided, gradient will be used instead of color. Example: linear-gradient(90deg, #FFD9A5, #FFA500)',
      },
    ],
  },
  {
    key: 'title',
    label: 'Title',
    type: types.OBJECT,
    children: [
      {
        key: 'text',
        label: 'Title Text',
        type: types.TEXT,
      },
      {
        key: 'color',
        label: 'Title Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'gradient',
        label: 'Title Gradient',
        type: types.TEXT,
        tooltip: 'CSS gradient string. If provided, gradient will be used instead of color. Example: linear-gradient(90deg, #FFD9A5, #FFA500)',
      },
    ],
  },
  {
    key: 'description',
    label: 'Description',
    type: types.OBJECT,
    children: [
      {
        key: 'text',
        label: 'Description Text',
        type: types.TEXTAREA,
        options: {
          rows: 6,
        } as any,
      },
      {
        key: 'color',
        label: 'Description Color',
        type: types.COLOR_PICKER,
      },
    ],
  },
  {
    key: 'animation',
    label: 'Animation',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enable Animation',
        type: types.BOOLEAN,
      },
      {
        key: 'type',
        label: 'Animation Type',
        type: types.ENUM,
        options: ['FADE', 'SLIDE', 'SCALE'],
      },
      {
        key: 'direction',
        label: 'Direction (for Slide)',
        type: types.ENUM,
        options: DIRECTIONS,
      },
      {
        key: 'duration',
        label: 'Duration (s)',
        type: types.TEXT,
      },
      {
        key: 'initialDelay',
        label: 'Initial Delay (s)',
        type: types.TEXT,
      },
      {
        key: 'stagger',
        label: 'Stagger Delay (s)',
        type: types.TEXT,
        tooltip: 'Delay between each item\'s animation start.',
      },
      {
        key: 'animateHeader',
        label: 'Animate Header',
        type: types.BOOLEAN,
      },
      {
        key: 'animateBlocks',
        label: 'Animate Content',
        type: types.BOOLEAN,
      },
    ],
  },
];

const defaultProps: Props = {
  background: {
    useBackgroundColor: false,
    backgroundColor: '#000000',
    desktopImage: 'https://genesis-cdn.ff.com/cxc10/dark-gold-streaks-default.jpg',
    tabletImage: '',
    mobileImage: '',
  },
  subtitle: {
    text: 'The C10 Index is more than just a crypto portfolio Index',
    color: '#827667',
  },
  title: {
    text: 'It\'s a Weathervane of Future Asset Value and Wealth Trends',
    color: '#FFD9A5',
    gradient: 'linear-gradient(91.11deg, #FFD9A5 8.89%, #AA9271 39.64%, #605240 82.09%)',
  },
  description: {
    text: 'Centered on Bitcoin and Ethereum, the C10 Index includes the top ten cryptocurrencies by free-float market capitalization, excluding stablecoins. It is calculated using market-cap weighted rules, with a maximum 50% allocation to any single asset. Each cryptocurrency\'s weight and price are dynamically adjusted according to market fluctuations, providing investors with a diversified and comprehensive reference for crypto allocation.',
    color: '#8B8B8B',
  },
  animation: {
    enabled: false,
    type: 'SLIDE',
    direction: 'UP',
    duration: 0.8,
    initialDelay: 0,
    stagger: 0.2,
    animateHeader: true,
    animateBlocks: true,
  },
};

export default {
  title: 'C10 Center Message',
  id: 'C10CenterMessage',
  propsSchema,
  defaultProps,
}
